// Code generated by apic; DO NOT EDIT.
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useMemo, type ChangeEvent } from "react";
import type { GraphQLOpMeta, AuthMeta } from "./types";
import type { ApicClient } from "../react/client";
import { HTTPError } from "../react/client";
import { getApiToken } from "../react/auth";
import { tryCanSend, effectiveAuthMode } from "./TryPanel";
import { AuthBadge } from "./AuthBadge";
import { SurfaceSamples } from "./SurfaceSamples";
import {
  graphqlCurlSample,
  graphqlGoSample,
  graphqlPythonSample,
  graphqlTsSample,
} from "./codegen/graphql";

/** Maximum response body characters (UTF-16 code units) shown in the panel (4 096 chars). */
const TRUNCATE_CHARS = 4096;

/** Truncate a string to TRUNCATE_CHARS characters (UTF-16 code units) and append a note when cut. */
function truncate(s: string): string {
  if (s.length <= TRUNCATE_CHARS) {
    return s;
  }
  return s.slice(0, TRUNCATE_CHARS) + "\n… (truncated at 4 096 chars)";
}

/**
 * GraphQLPanel renders an interactive explorer panel for the GraphQL surface
 * of the generated API.
 *
 * Props:
 *   ops       – the list of GraphQL operations from ServiceMetadata.graphql.
 *   client    – a ApicClient provided by the parent via useExplorerClient.
 *   baseUrl   – optional base URL prefix for code samples.
 *   className – extra CSS class appended to the root element.
 *
 * Each operation is listed with:
 *   - Field name + kind badge (query / mutation / subscription)
 *   - AuthBadge showing the full auth policy (roles, composite, mTLS, …)
 *   - Request schema name and response schema name
 *   - A code-sample strip (curl | Go | Python | TypeScript) via SurfaceSamples
 *   - A query editor <textarea> seeded with a minimal GraphQL document
 *   - A variables <textarea>
 *   - An Execute button (disabled + note when cert-required or token-absent)
 *
 * Execute for query/mutation: calls client[methodName](variables).
 * Subscribe/Unsubscribe for subscription ops: calls client.graphQLSubscribe<Field>()
 *   which returns a GraphQLSubscription<T> (graphql-transport-ws protocol over WebSocket).
 *   Connect is capability-guarded: if WebSocket is unavailable in the current runtime
 *   a clear note is rendered and Subscribe is disabled.
 *   Bounded frame log with cleanup on unmount mirrors WebSocketPanel lifecycle.
 *
 * No web-global dependencies at module load time (RN-safe).
 */
export function GraphQLPanel(props: {
  ops: GraphQLOpMeta[];
  client: ApicClient;
  baseUrl?: string;
  className?: string;
}): JSX.Element {
  const { ops, client, className, baseUrl } = props;
  const root = ["apic-ui-graphql-panel", className].filter(Boolean).join(" ");

  return (
    <div className={root}>
      {ops.length === 0 && (
        <div className="apic-ui-graphql-panel-empty">
          {"No GraphQL operations exposed."}
        </div>
      )}
      {ops.map((op) => (
        <GraphQLOpCard
          key={op.field}
          op={op}
          client={client}
          {...(baseUrl !== undefined ? { baseUrl } : {})}
        />
      ))}
    </div>
  );
}

/** Maximum number of subscription frame log entries retained. */
const MAX_SUB_LOG_ENTRIES = 200;

/**
 * gqlSubscribeIsAvailable returns true when WebSocket is available in the current
 * runtime. Checked at call time — NEVER at module load — so this module is safe
 * to import on React Native (Hermes), where WebSocket may be absent or injected late.
 */
function gqlSubscribeIsAvailable(client: ApicClient): boolean {
  const cfg = (client as any).config as { webSocketFactory?: unknown } | undefined;
  if (cfg && typeof cfg.webSocketFactory === "function") {
    return true;
  }
  return typeof WebSocket !== "undefined";
}

/**
 * GraphQLOpCard renders one GraphQL operation with its header, auth badge,
 * schema names, code samples, query editor, variables editor, and execute control.
 * For subscription ops, renders a Subscribe/Unsubscribe control with a bounded
 * frame log (mirrors WebSocketPanel lifecycle).
 */
function GraphQLOpCard(props: {
  op: GraphQLOpMeta;
  client: ApicClient;
  baseUrl?: string;
}): JSX.Element {
  const { op, client, baseUrl } = props;

  // A minimal GraphQL document seeded for the editor based on kind + field.
  const seedQuery = op.kind === "subscription"
    ? "subscription { " + op.field + " }"
    : op.kind === "mutation"
      ? "mutation { " + op.field + " }"
      : "query { " + op.field + " }";

  const [queryText, setQueryText] = useState<string>(seedQuery);
  const [varsText, setVarsText] = useState<string>("{}");
  const [loading, setLoading] = useState<boolean>(false);
  const [responseBody, setResponseBody] = useState<string | null>(null);
  const [statusLabel, setStatusLabel] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Subscription state — only used when op.kind === "subscription".
  const [subConnected, setSubConnected] = useState<boolean>(false);
  const [subConnecting, setSubConnecting] = useState<boolean>(false);
  const [subError, setSubError] = useState<string | null>(null);
  const [subLog, setSubLog] = useState<Array<{ id: number; ts: string; content: string }>>([]);
  const subCounterRef = useRef<number>(0);
  const subRef = useRef<{ close(): void } | null>(null);
  const subUnsubRef = useRef<(() => void) | null>(null);
  const subLogEndRef = useRef<HTMLDivElement | null>(null);

  // Re-evaluate token on every render so gating reflects the current token.
  const hasToken = getApiToken() !== null;
  const canSendResult = tryCanSend(op.auth, hasToken);

  const isSubscription = op.kind === "subscription";

  // Build code samples. Use useMemo so the samples are only rebuilt when the
  // query text, variables text, baseUrl, or op changes — not on every render.
  // NOTE: the sample context deliberately OMITS the live token so that the
  // generated code snippets use the "<token>" placeholder. This prevents the
  // real bearer token from appearing in copyable <code> DOM elements, defeating
  // TokenBar's password masking. The execute/subscribe paths authenticate
  // through the generated client (client[methodName] / graphQLSubscribe<Field>),
  // which reads the live token internally — so this component never needs to
  // capture the token itself. (R2-1 security fix.)
  const resolvedBase = baseUrl !== undefined && baseUrl !== "" ? baseUrl : "https://api.example.com";

  const graphqlSamples = useMemo(() => {
    let variables: Record<string, unknown> | undefined;
    if (varsText.trim() !== "" && varsText.trim() !== "{}") {
      try {
        const parsed = JSON.parse(varsText) as unknown;
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          variables = parsed as Record<string, unknown>;
        }
      } catch {
        variables = undefined;
      }
    }
    // Sample context: baseUrl + query + variables only — NO token/apiKey.
    // Builders fall back to "<token>"/"<api-key>" placeholders when absent.
    const ctx = {
      baseUrl: resolvedBase,
      query: queryText,
      ...(variables !== undefined ? { variables } : {}),
    };
    return {
      curl:       graphqlCurlSample(op, ctx),
      go:         graphqlGoSample(op, ctx),
      python:     graphqlPythonSample(op, ctx),
      typescript: graphqlTsSample(op, ctx),
    };
  }, [op, queryText, varsText, resolvedBase]);

  // Cleanup subscription on unmount.
  useEffect(() => {
    return () => {
      subUnsubRef.current?.();
      subUnsubRef.current = null;
      subRef.current?.close();
      subRef.current = null;
    };
  }, []);

  // Auto-scroll the subscription log to the newest entry.
  useEffect(() => {
    subLogEndRef.current?.scrollIntoView?.({ behavior: "smooth", block: "nearest" });
  }, [subLog]);

  function appendSubLog(content: string): void {
    subCounterRef.current += 1;
    const id = subCounterRef.current;
    const ts = new Date().toISOString();
    setSubLog((prev) => {
      const next = [...prev, { id, ts, content }];
      return next.length > MAX_SUB_LOG_ENTRIES ? next.slice(next.length - MAX_SUB_LOG_ENTRIES) : next;
    });
  }

  async function handleSubscribe(): Promise<void> {
    // Capability guard: check at click time, not module load.
    if (!gqlSubscribeIsAvailable(client)) {
      setSubError(
        "WebSocket is unavailable in this runtime. " +
        "Provide config.webSocketFactory to subscribe on React Native.",
      );
      return;
    }
    setSubConnecting(true);
    setSubError(null);
    try {
      // The generated client exposes graphQLSubscribe<TitleField>() per subscription.
      const subscribeKey = "graphQLSubscribe" + op.methodName.charAt(0).toUpperCase() + op.methodName.slice(1);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof (client as any)[subscribeKey] !== "function") {
        throw new Error(
          "Subscribe method '" + subscribeKey + "' not found on client. " +
          "The generated client may not expose this subscription.",
        );
      }
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const sub = await (client as any)[subscribeKey]() as {
        close(): void;
        subscribe(handler: (v: unknown) => void): () => void;
      };
      subRef.current = sub;
      setSubLog([]);
      setSubConnected(true);
      appendSubLog("Subscribed to " + op.field);

      const unsub = sub.subscribe((msg: unknown) => {
        let content: string;
        try {
          content = truncate(JSON.stringify(msg, null, 2));
        } catch {
          content = truncate(String(msg));
        }
        appendSubLog(content);
      });
      subUnsubRef.current = unsub;
    } catch (err: unknown) {
      subUnsubRef.current?.();
      subUnsubRef.current = null;
      subRef.current?.close();
      subRef.current = null;
      setSubConnected(false);
      const msg = err instanceof Error ? err.message : String(err);
      setSubError(msg);
    } finally {
      setSubConnecting(false);
    }
  }

  function handleUnsubscribe(): void {
    subUnsubRef.current?.();
    subUnsubRef.current = null;
    subRef.current?.close();
    subRef.current = null;
    setSubConnected(false);
    appendSubLog("Unsubscribed from " + op.field);
  }

  async function handleExecute(): Promise<void> {
    setLoading(true);
    setResponseBody(null);
    setStatusLabel(null);
    setErrorMsg(null);

    // Parse variables JSON.
    let variables: Record<string, unknown> = {};
    if (varsText.trim() !== "" && varsText.trim() !== "{}") {
      try {
        const parsed = JSON.parse(varsText) as unknown;
        if (parsed !== null && typeof parsed === "object" && !Array.isArray(parsed)) {
          variables = parsed as Record<string, unknown>;
        }
      } catch {
        setErrorMsg("Invalid variables JSON.");
        setLoading(false);
        return;
      }
    }

    try {
      // Prefer the typed per-op method on the client when it exists.
      const methodKey = op.methodName;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (typeof (client as any)[methodKey] === "function") {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        const result = await (client as any)[methodKey](variables) as unknown;
        setStatusLabel("200 OK");
        let pretty: string;
        try {
          pretty = truncate(JSON.stringify(result, null, 2));
        } catch {
          pretty = String(result);
        }
        setResponseBody(pretty);
      } else {
        // Fallback: POST the raw GraphQL document to /graphql via requestRaw.
        // H2/N-2: use effectiveAuthMode so cert-only leaves (mtls/cac/piv)
        // are skipped and a usable identity mode (jwt/cookie/api_key) is
        // returned instead of the first leaf verbatim.
        const resp = await client.requestRaw(
          "POST",
          "/graphql",
          effectiveAuthMode(op.auth),
          "json",
          [],
          { query: queryText, ...(Object.keys(variables).length > 0 ? { variables } : {}) },
        );
        const text = await resp.text();
        setStatusLabel(String(resp.status));
        let pretty: string;
        try {
          pretty = truncate(JSON.stringify(JSON.parse(text), null, 2));
        } catch {
          pretty = truncate(text);
        }
        setResponseBody(pretty);
      }
    } catch (err: unknown) {
      if (err instanceof HTTPError) {
        setStatusLabel(String(err.status));
        let errBody: string;
        try {
          errBody = truncate(JSON.stringify(err.body, null, 2));
        } catch {
          errBody = err.bodyText;
        }
        setResponseBody(errBody);
      } else if (err instanceof Error) {
        setErrorMsg(err.message);
      } else {
        setErrorMsg("An unexpected error occurred.");
      }
    } finally {
      setLoading(false);
    }
  }

  const isSuccess = statusLabel !== null && statusLabel.startsWith("2");
  const isError = statusLabel !== null && !statusLabel.startsWith("2");
  const isSubConnectDisabled = !canSendResult.enabled || subConnecting || subConnected;

  return (
    <div className="apic-ui-graphql-op-card">
      {/* Header: field name + kind badge */}
      <div className="apic-ui-graphql-op-header">
        <span className="apic-ui-graphql-op-name">{op.field}</span>
        <span className={"apic-ui-chip apic-ui-chip--kind apic-ui-chip--kind-" + op.kind}>
          {op.kind}
        </span>
      </div>

      {/* Auth badge — shows full composite/RBAC/mTLS policy */}
      <div className="apic-ui-graphql-op-auth">
        <AuthBadge auth={op.auth} className="apic-ui-graphql-op-auth-badge" />
      </div>

      {/* Schema names */}
      <div className="apic-ui-graphql-op-schemas">
        {op.requestSchema !== null && (
          <span className="apic-ui-graphql-op-schema-label">
            {"Request: "}
            <code>{op.requestSchema}</code>
          </span>
        )}
        {op.responseSchema !== null && (
          <span className="apic-ui-graphql-op-schema-label">
            {"Response: "}
            <code>{op.responseSchema}</code>
          </span>
        )}
      </div>

      {/* Code samples — curl / Go / Python / TypeScript */}
      <SurfaceSamples
        samples={graphqlSamples}
        className="apic-ui-graphql-op-samples"
      />

      {/* Subscription transport note + Subscribe/Unsubscribe control (G7) */}
      {isSubscription && (
        <div className="apic-ui-graphql-op-subscription-note">
          <span className="apic-ui-graphql-op-subscription-label">
            {"Subscriptions stream over WebSocket (graphql-transport-ws protocol). " +
             "Connect below to receive live frames via graphQLSubscribe" +
             op.methodName.charAt(0).toUpperCase() + op.methodName.slice(1) + "()."}
          </span>
          {/* Subscribe / Unsubscribe control */}
          <div className="apic-ui-graphql-op-sub-controls">
            {!subConnected ? (
              <button
                className={
                  "apic-ui-graphql-op-subscribe" +
                  (isSubConnectDisabled ? " apic-ui-graphql-op-subscribe--disabled" : "") +
                  (subConnecting ? " apic-ui-graphql-op-subscribe--connecting" : "")
                }
                disabled={isSubConnectDisabled}
                onClick={() => { void handleSubscribe(); }}
              >
                {subConnecting ? "Subscribing…" : "Subscribe"}
              </button>
            ) : (
              <button
                className="apic-ui-graphql-op-unsubscribe"
                onClick={handleUnsubscribe}
              >
                {"Unsubscribe"}
              </button>
            )}
            {/* Auth gate note for subscriptions */}
            {!canSendResult.enabled && canSendResult.note !== undefined && (
              <span className="apic-ui-graphql-op-sub-gate-note">{canSendResult.note}</span>
            )}
            {/* Connection status badge */}
            <span
              className={
                "apic-ui-chip apic-ui-chip--status" +
                (subConnected ? " apic-ui-chip--status-connected" : " apic-ui-chip--status-disconnected")
              }
            >
              {subConnected ? "subscribed" : "not subscribed"}
            </span>
            {/* Subscribe error */}
            {subError !== null && (
              <span className="apic-ui-graphql-op-sub-error">{subError}</span>
            )}
          </div>
          {/* Subscription frame log */}
          {subLog.length > 0 && (
            <div className="apic-ui-graphql-op-sub-log">
              <div className="apic-ui-graphql-op-sub-log-header">
                {"Events (" + subLog.length + " / " + MAX_SUB_LOG_ENTRIES + ")"}
              </div>
              <div className="apic-ui-graphql-op-sub-log-entries">
                {subLog.map((entry) => (
                  <div key={entry.id} className="apic-ui-graphql-op-sub-log-entry">
                    <span className="apic-ui-graphql-op-sub-log-ts">{entry.ts}</span>
                    <pre className="apic-ui-graphql-op-sub-log-content">
                      <code>{entry.content}</code>
                    </pre>
                  </div>
                ))}
                <div ref={subLogEndRef} className="apic-ui-graphql-op-sub-log-end" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Query editor — read-only for subscriptions (document is shown for reference) */}
      <div className="apic-ui-graphql-op-editor">
        <label
          className="apic-ui-graphql-op-editor-label"
          htmlFor={"apic-ui-gql-query-" + op.field}
        >
          {"Query"}
        </label>
        <textarea
          id={"apic-ui-gql-query-" + op.field}
          className="apic-ui-graphql-op-editor-textarea"
          rows={4}
          value={queryText}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { setQueryText(e.target.value); }}
          readOnly={isSubscription}
        />
      </div>

      {/* Variables editor — hidden for subscriptions (graphql-transport-ws subscriptions
          are established via the typed method with no inline variable injection here) */}
      {!isSubscription && (
        <div className="apic-ui-graphql-op-vars">
          <label
            className="apic-ui-graphql-op-vars-label"
            htmlFor={"apic-ui-gql-vars-" + op.field}
          >
            {"Variables (JSON)"}
          </label>
          <textarea
            id={"apic-ui-gql-vars-" + op.field}
            className="apic-ui-graphql-op-vars-textarea"
            rows={3}
            placeholder={"{}"}
            value={varsText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => { setVarsText(e.target.value); }}
          />
        </div>
      )}

      {/* Execute control — non-subscription ops only */}
      {!isSubscription && (
        <div className="apic-ui-graphql-op-actions">
          <button
            className={
              "apic-ui-graphql-op-execute" +
              (!canSendResult.enabled ? " apic-ui-graphql-op-execute--disabled" : "") +
              (loading ? " apic-ui-graphql-op-execute--loading" : "")
            }
            disabled={!canSendResult.enabled || loading}
            onClick={() => { void handleExecute(); }}
          >
            {loading ? "Executing…" : "Execute"}
          </button>
          {!canSendResult.enabled && canSendResult.note !== undefined && (
            <span className="apic-ui-graphql-op-note">{canSendResult.note}</span>
          )}
        </div>
      )}

      {/* Error message */}
      {errorMsg !== null && (
        <div className="apic-ui-graphql-op-error">
          <span className="apic-ui-graphql-op-error-msg">{errorMsg}</span>
        </div>
      )}

      {/* Response display */}
      {statusLabel !== null && (
        <div className="apic-ui-graphql-op-response">
          <span
            className={
              "apic-ui-graphql-op-status" +
              (isSuccess ? " apic-ui-graphql-op-status--success" : "") +
              (isError ? " apic-ui-graphql-op-status--error" : "")
            }
          >
            {statusLabel}
          </span>
          {responseBody !== null && (
            <pre className="apic-ui-graphql-op-response-body">
              <code>{responseBody}</code>
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
